import type { ApiResponse } from '@lombok/shared';
import { ApiError, GENERIC_ERROR_MESSAGE_ES } from './errors';

/**
 * Returns a bearer token (Cognito ID/access token) or null/undefined when the
 * user is signed out. Injected by the app so this package stays free of any
 * auth-SDK dependency. May be async (token refresh) or sync.
 */
export type TokenProvider = () => string | null | undefined | Promise<string | null | undefined>;

export interface ApiClientConfig {
  /** Base URL of the API Gateway stage, e.g. https://api.lombok.example. No trailing slash needed. */
  baseUrl: string;
  /** Injectable token getter. The client never reads tokens from storage itself. */
  getToken?: TokenProvider;
  /** Optional default request timeout in ms (default 20000). */
  timeoutMs?: number;
  /** Optional fetch override (tests / SSR). Defaults to global fetch. */
  fetchImpl?: typeof fetch;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Query params; undefined/null entries are skipped. */
  query?: Record<string, string | number | boolean | undefined | null>;
  /** JSON body; serialized automatically. */
  body?: unknown;
  /** Extra headers (merged over defaults). */
  headers?: Record<string, string>;
  /** Idempotency key for write endpoints (sent as `Idempotency-Key`). */
  idempotencyKey?: string;
  /** Per-request abort signal (merged with the timeout signal). */
  signal?: AbortSignal;
}

/**
 * Thin typed fetch wrapper. Responsibilities:
 *  - prepend the configured base URL and build query strings
 *  - inject `Authorization: Bearer <token>` when a token provider yields one
 *  - send/parse the JSON `ApiResponse<T>` envelope from @lombok/shared
 *  - unwrap `data` on success, throw `ApiError` on any failure (envelope or transport)
 *
 * It holds NO business authorization and NO secrets — the backend is the authority.
 */
export class ApiClient {
  private readonly baseUrl: string;
  private readonly getToken?: TokenProvider;
  private readonly timeoutMs: number;
  private readonly fetchImpl: typeof fetch;

  constructor(config: ApiClientConfig) {
    this.baseUrl = config.baseUrl.replace(/\/+$/, '');
    this.getToken = config.getToken;
    this.timeoutMs = config.timeoutMs ?? 20_000;
    this.fetchImpl = config.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  async get<T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'GET' });
  }

  async post<T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'POST', body });
  }

  async patch<T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'PATCH', body });
  }

  async put<T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'PUT', body });
  }

  async delete<T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'DELETE' });
  }

  /**
   * Core request. Returns the unwrapped `data` payload on success and throws
   * `ApiError` for every failure path so callers (and TanStack Query) have a
   * single error type to handle.
   */
  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const url = this.buildUrl(path, options.query);

    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...options.headers,
    };
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';
    if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;

    const token = this.getToken ? await this.getToken() : undefined;
    if (token) headers.Authorization = `Bearer ${token}`;

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), this.timeoutMs);
    if (options.signal) {
      // Propagate an externally-provided abort into our controller.
      options.signal.addEventListener('abort', () => controller.abort(), { once: true });
    }

    let res: Response;
    try {
      res = await this.fetchImpl(url, {
        method: options.method ?? 'GET',
        headers,
        body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
        signal: controller.signal,
      });
    } catch (cause) {
      const aborted = cause instanceof DOMException && cause.name === 'AbortError';
      throw new ApiError({
        code: aborted ? 'RATE_LIMITED' : 'INTERNAL',
        message: aborted
          ? 'La solicitud tardó demasiado. Verifica tu conexión e intenta de nuevo.'
          : 'No se pudo conectar con el servidor. Revisa tu conexión a internet.',
      });
    } finally {
      clearTimeout(timeout);
    }

    return this.parse<T>(res);
  }

  private buildUrl(path: string, query?: RequestOptions['query']): string {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const url = new URL(`${this.baseUrl}${normalizedPath}`);
    if (query) {
      for (const [key, value] of Object.entries(query)) {
        if (value !== undefined && value !== null) url.searchParams.set(key, String(value));
      }
    }
    return url.toString();
  }

  /** Parse the response body and reconcile it with the ApiResponse envelope. */
  private async parse<T>(res: Response): Promise<T> {
    let json: unknown;
    try {
      // 204 / empty bodies are valid for some writes.
      const text = await res.text();
      json = text ? (JSON.parse(text) as unknown) : undefined;
    } catch {
      throw new ApiError({
        code: 'INTERNAL',
        message: GENERIC_ERROR_MESSAGE_ES,
        status: res.status,
      });
    }

    const envelope = json as ApiResponse<T> | undefined;

    if (envelope && typeof envelope === 'object' && 'success' in envelope) {
      if (envelope.success) return envelope.data;
      throw new ApiError({
        code: envelope.error.code,
        message: envelope.error.message || GENERIC_ERROR_MESSAGE_ES,
        status: res.status,
        requestId: envelope.requestId,
        details: envelope.error.details,
      });
    }

    // Non-enveloped response: treat any non-2xx as an error, otherwise pass through.
    if (!res.ok) {
      throw new ApiError({
        code: 'INTERNAL',
        message: GENERIC_ERROR_MESSAGE_ES,
        status: res.status,
      });
    }
    return json as T;
  }
}
